import {
  type ApiResponse,
  idempotencyHeader
} from '../../api-client';

import {
  apiRequest
} from './client';

import type {
  InitiatePaymentInput,
  InitiatePaymentResult,
  InitiateManualEftResult,
  PresignPaymentProofInput,
  PaymentPresignResult,
  Payment
} from './types/invoices.types';

// Deliberately separate from invoices.service.ts - these hit the public,
// token-gated payment-link endpoints (no Bearer auth), not the authenticated
// dashboard ones. Keeping this its own narrow module keeps the "what's
// actually public" surface small and explicit rather than folded into the
// authenticated client.

export interface PublicInvoiceSummary {
  invoiceNo: string
  customerName: string
  totalCents: number
  balanceCents: number
  currencyCode: string
  invoiceStatus: string
  dueAt: string
  // The invoice's open bank deposit, so the page can open on it (reference,
  // and whether proof has already been received). Null when there is none.
  openBankDeposit?: {
    reference: string
    paymentId: number
    proofUploaded: boolean
  } | null
}

export async function getInvoiceByPaymentToken(token: string): Promise<ApiResponse<PublicInvoiceSummary>> {

  return apiRequest<ApiResponse<PublicInvoiceSummary>>(
    `/payments/pay/${ encodeURIComponent(token) }`
  );
}

export type InitiatePaymentByTokenInput = InitiatePaymentInput & {
  recaptchaToken: string
};

export async function initiatePaymentByToken(
  token: string,
  input: InitiatePaymentByTokenInput,
  idempotencyKey?: string
): Promise<ApiResponse<InitiatePaymentResult>> {

  return apiRequest<ApiResponse<InitiatePaymentResult>>(
    `/payments/pay/${ encodeURIComponent(token) }/initiate`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: idempotencyHeader(idempotencyKey)
    }
  );
}

// Manual-EFT counterpart - client pays via their own bank instead of a
// gateway, then uploads their own proof. No recaptcha token needed on the
// initiate call itself (recaptcha is already enforced route-side), but the
// route requires the header/action match - same token gate as above.

// recaptchaToken: billing runs verifyRecaptcha('pay_via_link') on this route
// exactly as on /initiate. It was never sent, so every bank-deposit request
// from a payment link failed with "reCAPTCHA token missing" (production,
// 2026-10-05).
export async function initiateEftByToken(
  token: string,
  recaptchaToken: string,
  idempotencyKey?: string
): Promise<ApiResponse<InitiateManualEftResult>> {

  return apiRequest<ApiResponse<InitiateManualEftResult>>(
    `/payments/pay/${ encodeURIComponent(token) }/initiate-eft`,
    {
      method: 'POST',
      body: JSON.stringify({
        recaptchaToken
      }),
      headers: idempotencyHeader(idempotencyKey)
    }
  );
}

export async function presignEftProofByToken(
  token: string,
  paymentId: number,
  input: PresignPaymentProofInput
): Promise<ApiResponse<PaymentPresignResult>> {

  return apiRequest<ApiResponse<PaymentPresignResult>>(
    `/payments/pay/${ encodeURIComponent(token) }/${ paymentId }/proof/presign`,
    {
      method: 'POST',
      body: JSON.stringify(input)
    }
  );
}

export async function confirmEftProofByToken(
  token: string,
  paymentId: number,
  fileKey: string
): Promise<ApiResponse<Payment>> {

  return apiRequest<ApiResponse<Payment>>(
    `/payments/pay/${ encodeURIComponent(token) }/${ paymentId }/proof/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({
        fileKey
      })
    }
  );
}
